import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Car, Users, Inbox, Settings as SettingsIcon, LogOut, Menu, X, UserCog, KeyRound, Building2, Clock, AlertTriangle, Lock, ShieldOff, MonitorSmartphone, Target } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { brandStyle, isAdminHost, BRAND } from "@/lib/brand";
import { BrandLogo } from "@/components/BrandLogo";
import { PoweredBy } from "@/components/VinconexMark";
import { Button } from "@/components/ui/button";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { SessionsDialog } from "@/components/SessionsDialog";
import { InstallPrompt } from "@/components/InstallPrompt";

const NAV = [
  { to: "/dashboard", label: "Inventory", icon: Car, end: true, testid: "nav-inventory" },
  { to: "/dashboard/specialists", label: "Specialists", icon: Users, testid: "nav-specialists" },
  { to: "/dashboard/leads", label: "Leads", icon: Inbox, testid: "nav-leads" },
  { to: "/dashboard/team", label: "Team", icon: UserCog, testid: "nav-team", adminOnly: true },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/dashboard/platform", label: "Platform", icon: Building2, testid: "nav-platform", ownerOnly: true },
  { to: "/dashboard/prospects", label: "Prospects", icon: Target, testid: "nav-prospects", ownerOnly: true },
];

export default function DashboardLayout() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [dealer, setDealer] = useState(null);

  useEffect(() => {
    api.get("/dealer").then(({ data }) => setDealer(data)).catch(() => setDealer(null));
  }, [user?.active_dealer_id, user?.dealer_id]);

  const isStaff = user?.platform_owner || user?.platform_admin;
  const staffOnly = user?.platform_admin && !user?.dealer_id && !user?.active_dealer_id;   // Vinconex staff with no dealership at all

  const doLogout = async () => {
    await logout();
    navigate(user?.platform_admin && !user?.dealer_id ? (isAdminHost() ? "/" : "/admin") : "/login");
  };

  const forgetDevices = async () => {
    try {
      await api.post("/auth/mfa/forget-devices");
      toast.success("Trusted devices cleared — every device will be asked for a sign-in code next time");
    } catch {
      toast.error("Couldn't clear trusted devices");
    }
  };

  const exitDealer = async () => {
    await api.post("/platform/exit");
    await refresh();
    navigate("/dashboard/platform");
  };

  const viewingOther = isStaff && user?.active_dealer_id && user.active_dealer_id !== user.dealer_id;
  const visible = NAV.filter((n) =>
    (!n.adminOnly || user?.role === "admin" || isStaff) && (!n.ownerOnly || isStaff) && (!staffOnly || n.ownerOnly));

  const STAFF_PATHS = ["/dashboard/platform", "/dashboard/prospects"];
  if (staffOnly && !STAFF_PATHS.includes(location.pathname)) return <Navigate to="/dashboard/platform" replace />;

  const SidebarContent = () => (
    <>
      <div className="px-5 py-5 border-b border-[#2A2E3D]">
        <a href={dealer?.slug ? `/d/${dealer.slug}` : "/"} target="_blank" rel="noreferrer" title="Open public page">
          <BrandLogo dealer={dealer} />
        </a>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {visible.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-[#0A8CF2]/15 text-white border border-[#0A8CF2]/40"
                  : "text-slate-400 hover:text-white hover:bg-slate-900 border border-transparent"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
            {n.to === "/dashboard/specialists" && dealer && dealer.addon_specialist_match !== "active" && (
              <Lock className="h-3 w-3 ml-auto text-amber-400/80" data-testid="nav-specialists-lock" title="Specialist Match add-on" />
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-[#2A2E3D]">
        <div className="px-3 mb-3">
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-widest font-mono-tech">
            {user?.platform_owner ? `${BRAND.owner} · platform owner` : user?.platform_admin ? `${BRAND.owner} · Vinconex staff` : user?.role}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setPwOpen(true)}
          data-testid="change-password-btn"
          className="w-full mb-2 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200 justify-start"
        >
          <KeyRound className="mr-2 h-4 w-4" /> Change password
        </Button>
        <Button
          variant="outline"
          onClick={() => setSessionsOpen(true)}
          data-testid="sessions-btn"
          title="See every device signed in to your account, or sign out everywhere"
          className="w-full mb-2 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200 justify-start"
        >
          <MonitorSmartphone className="mr-2 h-4 w-4" /> Where you're signed in
        </Button>
        {user?.mfa_enabled && (
          <Button
            variant="outline"
            onClick={forgetDevices}
            data-testid="forget-devices-btn"
            title="Every device will be asked for an emailed sign-in code next time"
            className="w-full mb-2 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200 justify-start"
          >
            <ShieldOff className="mr-2 h-4 w-4" /> Sign out of trusted devices
          </Button>
        )}
        <Button
          variant="outline"
          onClick={doLogout}
          data-testid="logout-btn"
          className="w-full border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200 justify-start"
        >
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </Button>
        <div className="px-3 pt-4">
          <PoweredBy />
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white flex" style={brandStyle(dealer?.accent_color)}>
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#2A2E3D] bg-[#0A0B0E] fixed inset-y-0">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 flex flex-col bg-[#0A0B0E] border-r border-[#2A2E3D]">
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-[#2A2E3D] h-14 flex items-center justify-between px-4">
          <BrandLogo dealer={dealer} compact />
          <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </header>

        {viewingOther && (
          <div data-testid="viewing-dealer-banner" className="bg-[#0A8CF2]/15 border-b border-[#0A8CF2]/40 px-5 py-2 text-sm flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><Building2 className="h-4 w-4 text-[#7FC3FF]" /> Viewing <b>{dealer?.name}</b> as {BRAND.owner} ({user?.platform_owner ? "Vinconex platform owner" : "Vinconex staff"})</span>
            <button onClick={exitDealer} data-testid="exit-dealer-btn" className="text-[#7FC3FF] hover:text-white font-semibold">Exit to Platform</button>
          </div>
        )}
        {dealer?.status === "pending" && (
          <div data-testid="pending-banner" className="bg-amber-950/40 border-b border-amber-800/50 px-5 py-2 text-sm text-amber-300 flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0" /> Your dealership is awaiting Vinconex approval. Set everything up now — public QR pages go live the moment you're approved.
          </div>
        )}
        {dealer?.status === "suspended" && (
          <div data-testid="suspended-banner" className="bg-red-950/40 border-b border-red-800/50 px-5 py-2 text-sm text-red-300 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" /> This dealership is suspended. Public pages are offline until Vinconex reactivates it.
          </div>
        )}

        <main className="p-5 sm:p-8 max-w-7xl">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <SessionsDialog open={sessionsOpen} onOpenChange={setSessionsOpen} />
      <InstallPrompt />
    </div>
  );
}
